<template>
    <div class="vx-pie-graph">
        <vx-select
            v-if="!isSingleSeries"
            v-model="localSeries"
            :options="series"
            text-key="name"
            id-key="name"
        />
        <vx-graph
            v-bind="$attrs"
            v-on="listeners"
            v-model="localValue"
            ref="chart"
            :type="chartType"
            :config="chartOptions"
            :show-data-labels="fullInterface"
            :full-interface="fullInterface"
            :series="[localSeries]"
            @dataPointSelection="onDataPointSelected"
            @animationEnd="updateSelectionTransformOrigin"
        >
            <template #tooltip="props" v-if="$scopedSlots.tooltip">
                <slot name="tooltip" v-bind="props" />
            </template>
        </vx-graph>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import _color from '@/assets/utils/color.js'
import chartMixins from './chartMixins.js'

export default {
    name: 'vx-pie-graph',
    mixins: [chartMixins],
    props: {
        fullInterface: {
            type: Boolean,
            default: false
        },
        value: {
            type: null
        }
    },
    watch: {
        localValue: {
            handler: function (newV) {
                if (!newV) {
                    return
                }
                var newId = this.getIdForDataPoint(newV)
                var seriesIndex = this.series.findIndex((series) => {
                    return !!series.data.first((dataPoint) => {
                        return this.getIdForDataPoint(dataPoint) == newId
                    })
                })
                if (seriesIndex >= 0) {
                    this.selectedSeriesIndex = seriesIndex
                }
            },
            immediate: true
        }
    },
    data() {
        return {
            selectedSeriesIndex: 0,
            fontFamily: '"Montserrat", Helvetica, Arial, sans-serif'
        }
    },
    computed: {
        ...mapState({ windowWidth: 'windowWidth' }),
        ...mapGetters({
            themeMode: 'user/themeMode'
        }),
        listeners() {
            var handlers = {
                ...this.$listeners
            }
            delete handlers.input
            return handlers
        },
        localValue: {
            get() {
                return this.value
            },
            set(v) {
                this.$emit('input', v)
            }
        },
        localSeries: {
            get() {
                if (!this.series || !this.series.length) {
                    return []
                }
                return this.series[this.selectedSeriesIndex || 0]
            },
            set(v) {
                this.selectedSeriesIndex = this.series.indexOf(v)
            }
        },
        chartOptions() {
            return {
                legend: this.legend,
                tooltip: {
                    theme: 'dark'
                },
                plotOptions: this.plotOptions,
                stroke: {
                    width: 0
                }
            }
        },
        chartType() {
            return this.fullInterface ? 'donut' : 'pie'
        },
        legend() {
            return {
                position: 'right',
                show: this.fullInterface,
                floating: false,
                markers: {
                    offsetY: 2
                },
                //labels: {
                formatter: function (val, opts) {
                    const maxLength = 15
                    if (val && val.length > maxLength) {
                        return val.substring(0, maxLength) + '...'
                    }
                    return val
                }
                //}
            }
        },
        plotOptions() {
            return {
                pie: {
                    donut: {
                        labels: {
                            show: this.fullInterface,
                            name: {
                                show: true,
                                fontSize: 20,
                                fontWeight: 'normal',
                                fontFamily: 'inherit'
                            },
                            value: {
                                show: true,
                                fontSize: 24,
                                fontWeight: 'bold',
                                fontFamily: 'inherit',
                                formatter: this.formatValue
                            },
                            total: {
                                show: true,
                                fontSize: 20,
                                fontWeight: 'normal',
                                fontFamily: 'inherit'
                            }
                        }
                    }
                }
            }
        }
    },
    methods: {
        formatValue(value, config) {
            return this.$refs.chart.formatValue(Number(value))
        },
        onDataPointSelected(e, ctx, config) {
            this.updateSelectionTransformOrigin()

            if (this.chartType == 'donut') {
                this.updateCenterLabels(config)
            }
        },
        updateCenterLabels(config) {
            var label = this.$el.querySelector('.apexcharts-text.apexcharts-datalabel-label')
            var value = this.$el.querySelector('.apexcharts-text.apexcharts-datalabel-value')
            var wrapper = this.$el.querySelector('.apexcharts-datalabels-group')

            if (!wrapper) {
                return
            }

            var selectedDataPoint = this.series[config.seriesIndex].data[config.dataPointIndex]
            if (!selectedDataPoint) {
                return
            }

            if (label) {
                label.innerHTML = selectedDataPoint.name
            }
            if (value) {
                value.innerHTML = this.formatValue(selectedDataPoint.value)
            }
            wrapper.style.opacity = 1
        },
        updateSelectionTransformOrigin() {
            this.$nextTick(() => {
                const slices = this.$el.querySelector(`.apexcharts-slices`)
                const selectedSlice = this.$el.querySelector(`path[selected='true']`)

                if (slices && selectedSlice) {
                    const bbox = slices.getBBox()
                    const originX = bbox.x + bbox.width / 2
                    const originY = bbox.y + bbox.height / 2

                    selectedSlice.style.transformOrigin = `${originX}px ${originY}px`

                    var wrapper = selectedSlice.parentElement
                    var container = wrapper.parentElement

                    if (!container) {
                        return
                    }
                    var label = container.querySelector('.apexcharts-datalabels')

                    if (label) {
                        container.insertBefore(wrapper, label)
                        return
                    }

                    container.append(selectedSlice)
                }
            })
        }
    }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/vuexy/_variables.scss';

.theme-dark {
    .vx-pie-graph {
        ::v-deep .apexcharts-pie-area {
            &[selected='true'] {
                stroke: white;
            }
        }
    }
}
.vx-pie-graph {
    ::v-deep {
        .apexcharts-pie-area {
            &[selected='true'] {
                transform: scale(1.05);
                fill-opacity: 1;
                stroke-width: 2;
                stroke: black;
                filter: none;
            }
        }
        .apexcharts-datalabels-group {
            transform-origin: 25% 50%;
        }
    }
}
</style>
